import { NotificationIcon } from "vega-icons";

import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import { NotificationCenter } from "@vega-core/components/NotificationCenter/NotificationCenter";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { CoInvestBadgeTriggerButton } from "@vega-modules/scenes/CoInvestments/components/CoInvestBadgeTriggerButton/CoInvestBadgeTriggerButton";
import { useNotificationCenterData } from "@vega-modules/scenes/Notifications/useNotificationCenterData";
import { useScopedUnreadCount } from "@vega-modules/scenes/Notifications/useScopedUnreadCount";

import { COINVEST_NOTIFICATION_PUBLISHER } from "../../constants/notificationConstants";

type Props = {
  metadata: Record<string, string>;
};

/**
 * Deal-page notification bell: opens the reused Notification Centre drawer,
 * scoped to this deal's co-invest notifications. The unread badge stays live
 * even while the drawer is closed (the list query only runs when open).
 */
export const CoInvestDealNotificationBell = ({ metadata }: Props) => {
  const { value: isOpen, setValue: setOpen } = useBoolean(false);

  const scope = {
    publisher: COINVEST_NOTIFICATION_PUBLISHER,
    metadata,
  };

  const notificationCenterProps = useNotificationCenterData(scope, {
    enabled: isOpen,
    readVisibleItemsOnly: true,
  });
  const unreadCount = useScopedUnreadCount(scope);

  const hasUnread = unreadCount > 0;

  return (
    <NotificationCenter<NotificationResponse>
      {...notificationCenterProps}
      onOpenChange={setOpen}
      open={isOpen}
    >
      <NotificationCenter.Trigger>
        <CoInvestBadgeTriggerButton
          aria-label={
            hasUnread
              ? m.notificationCenter_triggerUnreadAriaLabel({
                  count: unreadCount,
                })
              : m.notificationCenter_triggerAriaLabel()
          }
          count={unreadCount}
          dataTestId="coinvest-deal-notification-bell"
          icon={<NotificationIcon aria-hidden="true" className="h-5 w-5" />}
        />
      </NotificationCenter.Trigger>
      <NotificationCenter.Panel />
    </NotificationCenter>
  );
};
